<template>
  <span
    v-if="statusDescription"
    v-tooltipable
    class="max-w-3/4 overflow-hidden rounded-full px-2-5 py-1 text-xxs text-ellipsis whitespace-nowrap uppercase"
    :style="getPillStyle({ color: COLORS[colorCode] })"
  >
    {{ statusDescription }}
  </span>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { FullNotableType } from "@/enums/dealActivity";
import { getPillStyle } from "@/helpers/UI";
import { COLORS, STATUS_COLOR_CODES } from "@/helpers/constants";
import { WORKFLOW_TO_APP_STATUS } from "@/helpers/constants/workflow";

import type { ApplicationStatus } from "@/models/applications";
import type { NoteLogData } from "@/models/communicationLogs";
import type { IFlowRecord, IWorkflowStage } from "@/models/workflows";

const { getters } = useStore();

const { note } = defineProps<{
  note: NoteLogData;
}>();

const { t } = useI18n();

const flows = computed<IFlowRecord[]>(
  () => getters["workflows/activeTemplateFlow"]
);

const callDispositions = computed<Record<string, string>>(
  () => getters["options/callDispositions"]
);

const stages = computed<IWorkflowStage[]>(
  () => getters["workflows/activeTemplateStages"]
);

const noteFlow = computed(() =>
  flows.value.find((flow) => flow.id === note.stage)
);

const noteStage = computed(() =>
  stages.value.find((stage) => stage.id === noteFlow.value?.stage_id)
);

const statusDescription = computed(() => {
  if (note.notable_type === FullNotableType.application) {
    return noteFlow.value?.name || "";
  }
  if (note.notable_type === FullNotableType.business) {
    return t("DEALS.NOTES.BUSINESS_NOTE");
  }
  if (note.notable_type === FullNotableType.call && note.call_disposition) {
    return callDispositions.value[note.call_disposition] || "";
  }
  return "";
});

const colorCode = computed(() => {
  if (note.notable_type === FullNotableType.call) {
    return 0;
  }
  if (note.notable_type === FullNotableType.business) {
    return 1;
  }

  const stageType = noteStage.value?.type;
  if (stageType === undefined) {
    return 0;
  }
  const statusCode =
    WORKFLOW_TO_APP_STATUS[stageType as keyof typeof WORKFLOW_TO_APP_STATUS];
  return STATUS_COLOR_CODES[statusCode as ApplicationStatus];
});
</script>
